import {
  configureStore,
  combineReducers,
  Middleware,
  AnyAction,
  Action,
  ThunkAction,
} from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { setupListeners } from "@reduxjs/toolkit/query";

// Import reducers
import authReducer from "./authSlice";
import weatherReducer from "./weatherSlice";
import weatherPreferencesReducer from "./weatherPreferencesSlice";
import satellitesReducer from "./satellitesSlice";
import simulationReducer from "./simulationSlice";
import profileReducer from "./profileSlice";
import systemReducer from "./systemSlice";

// Determine if we're in production
const isProduction = process.env.NODE_ENV === "production";

// Logging middleware that's conditionally included in development
const logger: Middleware = (store) => (next) => (action: any) => {
  if (!isProduction) {
    if (action && action.type) {
      console.group(action.type);
      console.info("dispatching", action);
      const result = next(action);
      console.log("next state", store.getState());
      console.groupEnd();
      return result;
    }
  }
  return next(action);
};

// Error handling middleware
const errorMiddleware: Middleware = () => (next) => (action: any) => {
  try {
    return next(action);
  } catch (error) {
    console.error("Caught an error in Redux middleware:", error);

    // Send to error monitoring service in production
    if (isProduction && window.errorReporter) {
      window.errorReporter.captureException(error, {
        extra: {
          action: action.type,
          payload: action.payload,
        },
      });
    }

    // Dispatch error action for the UI to show a notification
    return next({
      type: "system/setError",
      payload: {
        message:
          error instanceof Error ? error.message : "An unknown error occurred",
        source: "redux",
        timestamp: new Date().toISOString(),
      },
    });
  }
};

// Performance monitoring middleware
const performanceMiddleware: Middleware =
  () => (next) => (action: any) => {
    if (!isProduction) return next(action);

    const startTime = performance.now();
    const result = next(action);
    const endTime = performance.now();

    const duration = endTime - startTime;

    // Log slow actions (> 16ms, roughly 1 frame @ 60fps)
    if (duration > 16) {
      console.warn(
        `Action ${action.type} took ${duration.toFixed(2)}ms to process`,
      );

      // Send to performance monitoring in production
      if (window.performanceMonitor) {
        window.performanceMonitor.trackPerformance({
          name: `redux_action_${action.type}`,
          duration,
          category: "redux",
        });
      }
    }

    return result;
  };

// Session timeout middleware
const sessionTimeoutMiddleware: Middleware =
  ({ dispatch, getState }) =>
  (next) =>
  (action: any) => {
    const result = next(action);

    // Check for 401 responses in rejected actions
    if (
      action.type.endsWith("/rejected") &&
      action.payload &&
      (action.payload.status === 401 ||
        action.payload.includes("Authentication"))
    ) {
      // Check if we're already logged out to prevent loops
      const authState = getState().auth;
      if (authState.isAuthenticated) {
        dispatch({ type: "auth/sessionExpired" });
        dispatch({
          type: "system/setNotification",
          payload: {
            type: "error",
            message: "Your session has expired. Please log in again.",
            duration: 5000,
          },
        });
      }
    }

    return result;
  };

// Define persist config for auth state
const authPersistConfig = {
  key: "auth",
  storage,
  whitelist: ["token", "email", "lastLogin"], // Only persist these fields
};

// Define persist config for app-level preferences
const preferencesPersistConfig = {
  key: "preferences",
  storage,
  whitelist: ["theme", "units"], // Only persist user preferences
};

// Define persist config for profile
const profilePersistConfig = {
  key: "profile",
  storage,
  whitelist: ["profile"], // Only persist profile data
};

// Define root reducer with persistence
const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  weather: weatherReducer,
  weatherPreferences: persistReducer(
    preferencesPersistConfig,
    weatherPreferencesReducer,
  ),
  satellites: satellitesReducer,
  simulation: simulationReducer,
  profile: persistReducer(profilePersistConfig, profileReducer),
  system: systemReducer,
});

// Define window interfaces for error handling and performance monitoring
declare global {
  interface Window {
    errorReporter?: {
      captureException(error: unknown, context?: Record<string, unknown>): void;
    };
    performanceMonitor?: {
      trackPerformance(data: {
        name: string;
        duration: number;
        category: string;
      }): void;
    };
  }
}

// Configure the store
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    const middlewares = getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for serializability checks
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    });

    // Add custom middleware
    middlewares.push(errorMiddleware);
    middlewares.push(performanceMiddleware);
    middlewares.push(sessionTimeoutMiddleware);

    // Add logger middleware only in development
    if (!isProduction) {
      middlewares.push(logger);
    }

    return middlewares;
  },
  devTools: !isProduction, // Enable Redux DevTools only in development
});

// Setup listeners for RTK Query (if you'll use it in the future)
setupListeners(store.dispatch);

// Create the persistor
export const persistor = persistStore(store);

// Define types for the store
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

export default { store, persistor };
